import React, { ChangeEvent, useEffect } from "react";
import { useTranslation } from "react-i18next";
import styled from "@emotion/styled";
import {
  FormControl,
  InputLabel,
  MenuItem,
  TextField,
  SelectChangeEvent,
  Box,
  Select,
  useTheme,
} from "@mui/material";
import { BankWrapper } from "@bn/api/apiv1";
import Gold from "@icons/myicons/gold-bars-shining-stars.svg?react";
import Cards from "@icons/myicons/cards.svg?react";
import Chart from "@icons/myicons/chart.svg?react";
import { BankSearchField } from "@components/common/forms/BankSearchField";
import { AccountTypeEnum, CreateOrUpdateAccountParams } from "@api";
import { MandatorTypography } from "@components/MandatorTypography";

export const StyledListItem = styled.div`
  display: flex;
  align-items: center;
  width: 100%;
  justify-content: space-between;

  img {
    margin-right: ${({ theme }) => theme.spacing(2)};
  }
`;

const StyledBox = styled(Box)`
  label {
    color: ${({ theme }) => theme.palette.text.secondary};
    font-size: 14px;
    line-height: 22px;
  }
`;

interface FieldsHideOptions {
  type?: boolean;
  name?: boolean;
  bank?: boolean;
}

export interface CreateAndEditAccountFormProps {
  accountValues: CreateOrUpdateAccountParams;
  defaultBank?: BankWrapper;
  hideFields?: FieldsHideOptions;
  onUpdateAccountValues: (params: Partial<CreateOrUpdateAccountParams>) => void;
  defaultType?: AccountTypeEnum;
}

export const CreateAndEditAccountForm = (
  props: CreateAndEditAccountFormProps
) => {
  const {
    defaultBank = null,
    defaultType,
    accountValues,
    hideFields = { type: false, name: false, bank: false },
    onUpdateAccountValues,
  } = props;
  const theme = useTheme();
  const { t } = useTranslation();

  const types = [
    { value: "01_depot", icon: <Chart /> },
    { value: "02_cash", icon: <Cards /> },
    { value: "05_material_assets", icon: <Gold /> },
  ];

  const handleSelectChange = (value: SelectChangeEvent<AccountTypeEnum>) => {
    const type = value.target.value as AccountTypeEnum;
    const enableImplicitTransactions = type === "01_depot";

    onUpdateAccountValues({ type, enableImplicitTransactions });
  };

  const handleNameChange = (event: ChangeEvent<HTMLInputElement>) => {
    onUpdateAccountValues({ name: event.target.value });
  };

  const handleBankClick = (value: BankWrapper | null) => {
    onUpdateAccountValues({
      bankName: value ? value.name : undefined,
      bic: value ? value.bic : undefined,
    });
  };

  useEffect(() => {
    if (defaultType) {
      onUpdateAccountValues({
        type: defaultType,
        enableImplicitTransactions: defaultType === "01_depot",
      });
    }
    // Should only run once to set the default value
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <>
      <StyledBox display="flex" flexDirection="column" gap={6}>
        {!hideFields.type && (
          <FormControl fullWidth>
            <InputLabel>{t("accounts.chooseType")}</InputLabel>
            <Select
              id="type"
              data-testid="account-type"
              value={accountValues?.type}
              onChange={handleSelectChange}
              renderValue={(selected) => (
                <Box display="flex" alignItems="center" gap={3}>
                  <Box display="flex" color={theme.palette.icon.secondary}>
                    {types.find(({ value }) => value === selected)?.icon}
                  </Box>
                  <MandatorTypography variant="body3">
                    {t(`accountTypes.${selected}`)}
                  </MandatorTypography>
                </Box>
              )}
            >
              {types.map(({ value, icon }) => (
                <MenuItem key={value} value={value} id={value}>
                  <Box display="flex" alignItems="center" gap={3}>
                    <Box display="flex" color={theme.palette.icon.secondary}>
                      {icon}
                    </Box>
                    <MandatorTypography color="textSecondary" variant="body3">
                      {t(`accountTypes.${value}`)}
                    </MandatorTypography>
                  </Box>
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        )}
        {!hideFields.name && (
          <TextField
            id="account-name"
            data-testid="account-name"
            fullWidth
            label={t("accounts.name")}
            value={accountValues?.name ?? ""}
            onChange={handleNameChange}
            placeholder={t("accounts.addManually.namePlaceholder")}
            slotProps={{
              htmlInput: {
                "data-testid": "account-name-input",
              },
            }}
          />
        )}
        {!hideFields.bank && (
          <BankSearchField
            title="accounts.addManually.bank"
            placeholder="accounts.addManually.bankPlaceholder"
            onBankClick={handleBankClick}
            defaultBank={defaultBank}
          />
        )}
      </StyledBox>
    </>
  );
};
